import {
  useCallback,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';
import Cookies from 'js-cookie';
import usePrevious from './usePrevious';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { getFontWeightStyle } from 'utils/fonts';
import { ProductDetailsType } from '../types/ProductDetailsType';
import { useAPI } from 'utils/api';

function useProductDetails(
  slug: string | null,
  token: string | null = null,
  previewEvent: any,
  authFetched: boolean,
  registrationId: string | null
): [
  ProductDetailsType | null,
  () => void,
  boolean,
  Dispatch<SetStateAction<ProductDetailsType | null>>
] {
  const [productLoading, setProductLoading] = useState<boolean>(false);
  const [productDetails, setProductDetails] =
    useState<ProductDetailsType | null>(null);
  const { setButtonStyle, setInputStyle, setMenuStyle } = useThemeContext();
  const country: string = Cookies.get(`countryCode`) || 'US';
  const previousEvent: any = usePrevious(previewEvent);
  const fetchProductRef = useRef<any>(null);
  const hasShellData = useRef(false);

  useEffect(() => {
    if (productDetails?.brand?.id) {
      const fonts = productDetails?.brand?.fonts || [];
      let fontCSS = fonts.reduce(
        (css: string, fontObject: any) =>
          css.concat(`
            @font-face {
              font-family: brandFont;
              src: url(${fontObject.src});
              font-weight: ${getFontWeightStyle(fontObject.name).weight};
              font-style: ${getFontWeightStyle(fontObject.name).style};
              font-display: swap;
            }
          `),
        ''
      );
      const style = document.createElement('style');
      style.innerHTML = fontCSS;
      document.head.appendChild(style);
    }
  }, [productDetails?.brand?.id, slug, productDetails?.brand.fonts]);

  const onSuccess = useCallback(
    (incoming: any) => {
      const { modules } = incoming;

      const leadModule = modules[0] || [];
      let moduleCopy = [...modules];

      setProductDetails((prev) => {
        if (window !== window.top)
          return {
            ...incoming,
            modules: moduleCopy,
            leadModule,
          };

        const incomingImage = incoming?.product?.image;
        const incomingSmImage = incoming?.product?.smsOptIn?.image;
        const prevImage = prev?.product?.image;
        const prevSmsImage = prev?.product?.smsOptIn?.image;

        const mediaUnchanged =
          hasShellData.current &&
          prev &&
          incomingImage === prevImage &&
          incomingSmImage === prevSmsImage;

        if (mediaUnchanged) {
          return {
            ...prev,
            ...incoming,
            product: {
              ...incoming.product,
              image: prev.product.image,
              smsOptIn: prev.product.smsOptIn,
            },
            modules: moduleCopy,
            leadModule,
          };
        }

        return {
          ...incoming,
          modules: moduleCopy,
          leadModule,
        };
      });

      if (incoming?.product?.customTheme) {
        setButtonStyle(incoming?.product?.customThemeConfig?.buttonStyle);
        setInputStyle(incoming?.product?.customThemeConfig?.inputStyle);
        setMenuStyle(incoming?.product?.customThemeConfig?.menuStyle);
      } else {
        setButtonStyle(incoming?.brand?.buttonStyle);
        setInputStyle(incoming?.brand?.inputStyle);
        setMenuStyle(incoming?.brand?.menuStyle);
      }
      console.log(
        `[ProductDetails] Data RECEIVED at ${Date.now()}ms | hasShellData=${
          hasShellData.current
        } | isRefetch=${hasShellData.current}`
      );
      hasShellData.current = true;
      setProductLoading(false);
      if (fetchProductRef.current) {
        fetchProductRef.current = null;
      }
    },
    [setButtonStyle, setInputStyle, setMenuStyle, setProductLoading]
  );

  const onError = useCallback(
    (error: string) => {
      setProductLoading(false);

      if (fetchProductRef.current) {
        fetchProductRef.current = null;
      }

      // Check if error indicates 404
      try {
        const parsed = JSON.parse(error);
        if (parsed?.status === 404) {
          window.location.href = '/404';
        }
      } catch {
        // If not JSON, check for 404 in string
        // if (error.includes('404')) {
        //   window.location.href = '/404';
        // }
      }
    },
    [setProductLoading]
  );

  // @ts-ignore
  const [getProduct] = useAPI(
    {
      method: 'GET',
      endpoint: `products/${slug}${
        registrationId ? `/${registrationId}` : ''
      }?country=${country}`,
      onError,
    },
    token,
    true,
    true
  );

  const getProductWithLoading = useCallback(
    async (...args: unknown[]) => {
      if (!slug) {
        return;
      }

      if (fetchProductRef.current) {
        return fetchProductRef.current;
      }

      if (!hasShellData.current) {
        setProductLoading(true);
      }

      fetchProductRef.current = getProduct(...args);

      // @ts-ignore
      const cache = await fetchProductRef.current;

      if (cache) {
        onSuccess(cache);
      }

      return;
    },
    [getProduct, onSuccess, slug]
  );

  // Use server-injected product data if available (eliminates API round trip)
  const ssrDataConsumed = useRef(false);
  useEffect(() => {
    if (ssrDataConsumed.current) return;
    const injected = (window as any).__PRODUCT_DATA__;
    if (injected && slug) {
      ssrDataConsumed.current = true;
      delete (window as any).__PRODUCT_DATA__;
      console.log(
        `[ProductDetails] SSR data USED at ${Date.now()}ms — skipping shell fetch`
      );
      onSuccess(injected);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Shell fetch: fires when slug is available and no SSR data was injected
  useEffect(() => {
    if (ssrDataConsumed.current) return;
    if (window !== window.top) return;
    if (
      slug &&
      !window.location.pathname.includes('/p/') &&
      !window.location.pathname.includes('/s/')
    ) {
      console.log(
        `[ProductDetails] Shell fetch FIRED at ${Date.now()}ms | authFetched=${authFetched} | token=${!!token}`
      );
      getProductWithLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Auth-aware refetch: after auth resolves, refetch with token for signed-in users
  useEffect(() => {
    if (!authFetched || !token) return;
    if (
      slug &&
      !window.location.pathname.includes('/p/') &&
      !window.location.pathname.includes('/s/')
    ) {
      console.log(
        `[ProductDetails] Auth refetch FIRED at ${Date.now()}ms | authFetched=${authFetched} | token=${!!token}`
      );
      fetchProductRef.current = null;
      getProductWithLoading(null, token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, authFetched, token]);

  // /s/ routes: fetch if productDetails is missing (handles hard refresh)
  useEffect(() => {
    if (
      slug &&
      window.location.pathname.includes('/s/') &&
      !productDetails &&
      !productLoading
    ) {
      getProductWithLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, productDetails, productLoading]);

  useEffect(() => {
    if (registrationId) getProductWithLoading(null, token);
  }, [getProductWithLoading, registrationId]);

  useEffect(() => {
    if (previewEvent && previewEvent.type === 'product') {
      if (
        previousEvent &&
        previousEvent.type === 'product' &&
        previousEvent.productDetails &&
        JSON.stringify(previousEvent.productDetails) ===
          JSON.stringify(previewEvent.productDetails)
      ) {
        return;
      }

      setTimeout(() => {
        onSuccess(previewEvent.productDetails);
      });
    }
  }, [previewEvent, onSuccess]);

  return [
    productDetails,
    getProductWithLoading,
    productLoading,
    setProductDetails,
  ];
}

export default useProductDetails;
